'use client'

import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

export type ConsentChannel = 'sms' | 'email'

export type ConsentSource = 'in_person' | 'phone_call' | 'web_form' | 'email' | 'written' | 'other'

export const CONSENT_SOURCES: { value: ConsentSource; label: string }[] = [
  { value: 'in_person', label: 'In person' },
  { value: 'phone_call', label: 'Phone call' },
  { value: 'web_form', label: 'Web form' },
  { value: 'email', label: 'Email' },
  { value: 'written', label: 'Written / paper' },
  { value: 'other', label: 'Other' },
]

export type ConsentChipProps = {
  channel: ConsentChannel
  /** True when consent is recorded (sms_opt_in for SMS, !email_opt_out for email). */
  optedIn: boolean
  /** ISO timestamp of when consent was recorded (if optedIn). */
  optedInAt?: string | null
  /** The source of consent (if optedIn). */
  source?: string | null
  /** The phone/email currently in the field — disables the chip when blank. */
  fieldHasValue: boolean
  /** Fired when the user confirms consent. Parent saves to DB. */
  onConfirm: (payload: { source: string; note?: string }) => Promise<void> | void
  /** Fired when the user revokes consent. */
  onRevoke?: () => Promise<void> | void
  /** Compact vs regular size. Compact is for inline contexts (ContactDetail). */
  size?: 'sm' | 'xs'
  /** When `false`, chip is informational only (no click-to-confirm popover). */
  editable?: boolean
}

/**
 * The popover half of ConsentChip, rendered into document.body.
 *
 * It cannot be a plain `absolute` child of the chip: both call sites sit
 * inside cards that clip (`SectionCard` in QuoteBuilder is
 * `rounded-xl … overflow-hidden`, there to clip its own corners), so an
 * absolutely-positioned panel was cut off mid-sentence and painted as a
 * stray white box over the next field. A portal escapes every clipping
 * ancestor; the trade is that fixed coordinates have to be recomputed as the
 * page scrolls, which is what the listeners below do.
 *
 * Positioned below the trigger, flipping above when there isn't room and
 * clamping to the viewport so it can never render off-screen.
 *
 * Note the colors below carry fallback chains. These are shared-ui SEMANTIC
 * tokens, defined in shared-ui/theme/tokens.css — which the portal does not
 * import (it loads ui-admin's shadcn token sheet instead). Unqualified, every
 * one of them resolved to nothing in the portal: the panel had no background
 * at all and the "OK" pill lost its emerald fill. The chain resolves to the
 * portal's equivalent shadcn token, then to the storefront token's own literal
 * value, so the component looks the same in both apps.
 */
function PopoverPanel({
  anchorRef, panelRef, className, children,
}: {
  anchorRef: React.RefObject<HTMLElement | null>
  panelRef: React.RefObject<HTMLDivElement | null>
  className: string
  children: React.ReactNode
}) {
  // Position is written straight to the node rather than held in state. Two
  // reasons: a scroll listener that setState'd would re-render this subtree on
  // every scroll tick, and setState inside a layout effect is what
  // react-hooks/set-state-in-effect (a CI error at --max-warnings 0) exists to
  // prevent. The panel renders hidden at 0,0 and `place` reveals it once it has
  // real coordinates, so there's no flash in the corner.
  const place = useCallback(() => {
    const anchor = anchorRef.current
    const el = panelRef.current
    if (!anchor || !el) return
    const r = anchor.getBoundingClientRect()
    const w = el.offsetWidth
    const h = el.offsetHeight
    // Start-aligned to the chip, not end-aligned. Both chips sit near the LEFT
    // edge of their card, so the old `right-0` threw a 240-280px panel leftward
    // out of the card and over the line-items table. Falls back to end-aligned
    // when start-aligning would run off the right of the viewport.
    let left = r.left
    if (left + w > window.innerWidth - 8) left = r.right - w
    if (left + w > window.innerWidth - 8) left = window.innerWidth - 8 - w
    if (left < 8) left = 8
    let top = r.bottom + 4
    if (top + h > window.innerHeight - 8 && r.top - h - 4 > 8) top = r.top - h - 4
    el.style.left = `${left}px`
    el.style.top = `${top}px`
    el.style.visibility = 'visible'
  }, [anchorRef, panelRef])

  // Layout effect so the first paint is already in the right place — a
  // useEffect here shows one frame at the top-left corner.
  useLayoutEffect(place, [place])

  useEffect(() => {
    // Bind to the anchor's actual scroll parents, not just window. Scroll
    // events don't bubble, and the chip scrolls inside `main.overflow-auto`,
    // not the document — so a window listener alone is betting on the capture
    // path. Subscribing to the real scrollers is what floating-ui's autoUpdate
    // does, and it doesn't depend on that bet.
    const targets: (Element | Window)[] = [window]
    for (let el = anchorRef.current?.parentElement; el; el = el.parentElement) {
      const o = getComputedStyle(el).overflow + getComputedStyle(el).overflowY
      if (/auto|scroll|overlay/.test(o)) targets.push(el)
    }
    targets.forEach(t => t.addEventListener('scroll', place, { passive: true }))
    window.addEventListener('resize', place)
    return () => {
      targets.forEach(t => t.removeEventListener('scroll', place))
      window.removeEventListener('resize', place)
    }
  }, [place, anchorRef])

  // SSR guard: shared-ui is imported by the Next storefront, where a 'use
  // client' component still renders on the server and has no document.
  if (typeof document === 'undefined') return null

  return createPortal(
    <div
      ref={panelRef}
      className={className}
      style={{ position: 'fixed', top: 0, left: 0, visibility: 'hidden' }}
    >
      {children}
    </div>,
    document.body,
  )
}

/**
 * Consent status chip + inline confirmation popover. Used below every email
 * and phone field to make the TCPA / CAN-SPAM state visible and editable.
 *
 * Visual language:
 *   - Emerald  — consent recorded (SMS opted in / email not opted out)
 *   - Amber    — consent not yet confirmed (the important interactive state)
 *   - Gray     — field has no value yet, or not applicable
 *
 * Clicking an amber chip opens a small popover with:
 *   - "Customer gave explicit consent" checkbox
 *   - Source dropdown (in_person / phone_call / web_form / email / written / other)
 *   - Free-text "Other" input when Other is selected
 *   - Confirm button (disabled until the checkbox + source are set)
 */
export default function ConsentChip({
  channel,
  optedIn,
  optedInAt,
  source,
  fieldHasValue,
  onConfirm,
  onRevoke,
  size = 'xs',
  editable = true,
}: ConsentChipProps) {
  const [open, setOpen] = useState(false)
  const [acknowledged, setAcknowledged] = useState(false)
  const [chosenSource, setChosenSource] = useState<ConsentSource | ''>('')
  const [otherNote, setOtherNote] = useState('')
  const [saving, setSaving] = useState(false)
  const rootRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)
  // The panel is portalled to document.body, so it is NOT inside rootRef —
  // outside-click has to check it separately or every click inside the
  // popover (the checkbox, the source select) would dismiss it.
  const panelRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    function handler(e: MouseEvent) {
      const t = e.target as Node
      if (rootRef.current?.contains(t)) return
      if (panelRef.current?.contains(t)) return
      closePopover()
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  function closePopover() {
    setOpen(false)
    setAcknowledged(false)
    setChosenSource('')
    setOtherNote('')
  }

  async function handleConfirm() {
    if (!acknowledged || !chosenSource) return
    const payload = {
      source: chosenSource === 'other' ? `other:${otherNote.trim()}` : chosenSource,
      note: chosenSource === 'other' ? otherNote.trim() : undefined,
    }
    setSaving(true)
    try {
      await onConfirm(payload)
      closePopover()
    } catch {
      // handler surfaces its own error feedback; keep the popover open for retry
    } finally {
      setSaving(false)
    }
  }

  // Size variants
  const pillBase = size === 'xs'
    ? 'text-[10px] px-1.5 py-0.5'
    : 'text-xs px-2 py-0.5'

  const channelLabelVerb = channel === 'sms' ? 'SMS' : 'Marketing email'

  // Empty-field state: informational gray
  if (!fieldHasValue) {
    return (
      <span className={`inline-flex items-center gap-1 rounded-full font-medium bg-[var(--color-surface-muted,var(--color-muted,#f3f4f6))] text-[var(--color-text-muted,var(--color-muted-foreground,#9ca3af))] ${pillBase}`}>
        {channelLabelVerb} consent pending
      </span>
    )
  }

  // Opted-in state: emerald, click to reveal detail + revoke option
  if (optedIn) {
    const sourceLabel = source
      ? (source.startsWith('other:')
          ? source.slice(6) || 'Other'
          : CONSENT_SOURCES.find(s => s.value === source)?.label ?? source)
      : null
    return (
      <div ref={rootRef} className="relative inline-block">
        <button
          ref={triggerRef}
          type="button"
          disabled={!editable}
          onClick={() => editable && setOpen(o => !o)}
          className={`inline-flex items-center gap-1 rounded-full font-medium bg-[var(--color-success-surface,#ecfdf5)] text-[var(--color-success-strong,#047857)] hover:opacity-90 transition-colors ${pillBase} ${editable ? 'cursor-pointer' : 'cursor-default'}`}
        >
          <span className="inline-block w-1.5 h-1.5 rounded-full bg-[var(--color-success-strong,#047857)]" />
          {channelLabelVerb} OK
        </button>
        {open && (
          <PopoverPanel
            anchorRef={triggerRef}
            panelRef={panelRef}
            className="z-30 bg-[var(--color-bg-primary,var(--color-popover,#ffffff))] border border-[var(--color-border)] rounded-lg shadow-lg p-3 min-w-[240px]"
          >
            <p className="text-xs text-[var(--color-text-secondary,var(--color-muted-foreground,#6b7280))] font-medium">{channelLabelVerb} consent on file</p>
            {sourceLabel && <p className="text-xs text-[var(--color-text-muted,var(--color-muted-foreground,#9ca3af))] mt-0.5">Source: {sourceLabel}</p>}
            {optedInAt && <p className="text-xs text-[var(--color-text-muted,var(--color-muted-foreground,#9ca3af))] mt-0.5">Recorded: {new Date(optedInAt).toLocaleDateString('en-US', { timeZone: 'UTC' })}</p>}
            {onRevoke && (
              <button
                type="button"
                onClick={async () => {
                  try { await onRevoke(); closePopover() }
                  catch { /* handler surfaces its own error feedback; keep the popover open */ }
                }}
                className="mt-2 text-xs text-[var(--color-error-strong,#be123c)] hover:opacity-80 underline"
              >
                Revoke consent
              </button>
            )}
          </PopoverPanel>
        )}
      </div>
    )
  }

  // Not-opted-in state: amber, click to open confirm popover
  return (
    <div ref={rootRef} className="relative inline-block">
      <button
        ref={triggerRef}
        type="button"
        disabled={!editable}
        onClick={() => editable && setOpen(o => !o)}
        className={`inline-flex items-center gap-1 rounded-full font-medium bg-[var(--color-warning-surface,#fffbeb)] text-[var(--color-warning-strong,#b45309)] hover:opacity-90 transition-colors ${pillBase} ${editable ? 'cursor-pointer' : 'cursor-default'}`}
      >
        <span>⚠</span>
        Confirm {channelLabelVerb.toLowerCase()} consent
      </button>
      {open && (
        <PopoverPanel
          anchorRef={triggerRef}
          panelRef={panelRef}
          className="z-30 bg-[var(--color-bg-primary,var(--color-popover,#ffffff))] border border-[var(--color-border)] rounded-lg shadow-lg p-3 w-[280px]"
        >
          <p className="text-xs font-medium text-[var(--color-text-primary,var(--color-foreground,#111827))]">Confirm {channel === 'sms' ? 'SMS' : 'marketing email'} consent</p>
          <p className="text-[11px] text-[var(--color-text-muted,var(--color-muted-foreground,#9ca3af))] mt-0.5">
            {channel === 'sms'
              ? 'TCPA requires explicit opt-in for marketing SMS.'
              : 'Customer gave permission to send marketing emails.'}
          </p>
          <label className="flex items-start gap-2 mt-2 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={acknowledged}
              onChange={e => setAcknowledged(e.target.checked)}
              className="mt-0.5"
            />
            <span className="text-xs text-[var(--color-text-secondary,var(--color-muted-foreground,#6b7280))]">
              Customer gave explicit consent to receive {channel === 'sms' ? 'SMS messages' : 'marketing emails'}.
            </span>
          </label>
          <div className="mt-2">
            <label className="text-[11px] font-medium text-[var(--color-text-secondary,var(--color-muted-foreground,#6b7280))]">How was consent obtained?</label>
            <select
              value={chosenSource}
              onChange={e => setChosenSource(e.target.value as ConsentSource)}
              className="mt-0.5 w-full border border-[var(--color-border)] rounded px-2 py-1 text-xs"
            >
              <option value="">Select...</option>
              {CONSENT_SOURCES.map(s => (
                <option key={s.value} value={s.value}>{s.label}</option>
              ))}
            </select>
          </div>
          {chosenSource === 'other' && (
            <div className="mt-2">
              <input
                type="text"
                value={otherNote}
                onChange={e => setOtherNote(e.target.value)}
                placeholder="Describe..."
                className="w-full border border-[var(--color-border)] rounded px-2 py-1 text-xs"
                autoFocus
              />
            </div>
          )}
          <div className="mt-3 flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={closePopover}
              className="text-xs text-[var(--color-text-muted,var(--color-muted-foreground,#9ca3af))] hover:text-[var(--color-text-secondary,var(--color-muted-foreground,#6b7280))] px-2 py-1"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleConfirm}
              disabled={!acknowledged || !chosenSource || (chosenSource === 'other' && !otherNote.trim()) || saving}
              className="text-xs bg-[var(--color-cta,var(--color-primary,#2563eb))] text-[var(--color-cta-text,var(--color-primary-foreground,#ffffff))] rounded px-2.5 py-1 hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? 'Saving…' : 'Confirm'}
            </button>
          </div>
        </PopoverPanel>
      )}
    </div>
  )
}
